import React, { useMemo, useState } from 'react';

import { FormField, FormControl, FormMessage, Form, FormItem } from '../../ui/form';

import { useForm, useWatch } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Calendar } from '../../ui/calendar';

import { Input } from '../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, CircleX } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { DialogClose } from '@radix-ui/react-dialog';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const formSchema = z.object({
  status: z.enum(['MISSING', 'COMPLETED'], { required_error: 'Status is required' }),
  date: z.date().nullable().optional(),
  time: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const CriticalDelayedOrderActionDialog = ({
  order,
  close,
  takeAction,
}: {
  order: any;
  close: () => void;
  takeAction: (
    orderId: string,
    action: 'MARKED_AS_COMPLETED' | 'MARKED_AS_MISSING',
    extraOptions?: { notes: string; date: Date },
  ) => void;
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      notes: '',
      status: undefined,
      time: undefined,
      date: undefined,
    },
  });

  const status = useWatch({ name: 'status', control: form.control });
  const fieldsDisabled = useMemo(() => status !== 'COMPLETED', [status]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleConfirm = (values) => {
    const action = values.status === 'COMPLETED' ? 'MARKED_AS_COMPLETED' : 'MARKED_AS_MISSING';
    const [h, m] = values?.time?.split(':') || [0, 0];
    const date = new Date(values.date);
    date.setHours(h);
    date.setMinutes(m);

    const extraOptions = values.status === 'COMPLETED' ? { notes: values.notes, date } : undefined;

    takeAction(order.orderId, action, extraOptions);
    form.reset();
    close();
  };
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && close()}>
      <DialogContent className="md:min-w-[600px]">
        <DialogHeader className="mb-8">
          <DialogTitle>Resolve this critically delayed order</DialogTitle>
          <DialogDescription className="pb-8">Please mark this order as missing or as delivered</DialogDescription>
          <DialogDescription asChild>
            <Card>
              <CardContent className="pt-4">
                <p>
                  <strong>{order?.deliveryName}</strong>
                </p>
                <div className="flex justify-between">
                  <div>{order?.deliveryAddress.split(',').map((l) => <p key={l}>{l}</p>)}</div>
                  <div className="bg-border w-[1px] mx-4"></div>
                  <div className="flex">
                    <div className="mr-2">
                      <p>Order Ref:</p>
                      <p>Barcode:</p>
                      <p>Order ID:</p>
                    </div>
                    <div>
                      <p>{order?.orderRef}</p>
                      <p>{order?.barcode}</p>
                      <p>{order?.orderId}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleConfirm)}>
            <div className="flex justify-between items-baseline">
              <FormField
                name="status"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="mb-4 min-w-[130px]">
                            <SelectValue placeholder="Resolve order as..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem className="cursor-pointer hover:bg-slate-100" value="MISSING">
                            Missing
                          </SelectItem>
                          <SelectItem className="cursor-pointer hover:bg-slate-100" value="COMPLETED">
                            Completed
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <Popover
                      modal={true}
                      open={calendarOpen}
                      onOpenChange={(v) => !fieldsDisabled && setCalendarOpen(v)}
                    >
                      <PopoverTrigger asChild>
                        <div className="flex items-center">
                          <Button
                            variant={'outline'}
                            type="button"
                            className={cn(
                              'w-[240px] pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground',
                              fieldsDisabled && 'cursor-not-allowed opacity-50',
                            )}
                          >
                            {field.value ? format(field.value, 'PPP') : <span>Date of delivery...</span>}
                            {!field.value ? (
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            ) : (
                              <div
                                className="ml-auto h-4 w-4"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  form.setValue('date', null);
                                }}
                              >
                                <CircleX className="opacity-50" />
                              </div>
                            )}
                          </Button>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <FormControl>
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(d) => {
                              field.onChange(d);
                              setCalendarOpen(false);
                            }}
                          />
                        </FormControl>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
              <p className={cn(fieldsDisabled && 'text-slate-300')}>at</p>
              <FormField
                name="time"
                control={form.control}
                disabled={fieldsDisabled}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mb-4 text-center"
                        type="time"
                        placeholder="Set time..."
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              name="notes"
              control={form.control}
              disabled={!status}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input className="mb-4" placeholder="Add (rider) comment..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary" onClick={() => form.reset()}>
                  Cancel
                </Button>
              </DialogClose>

              <Button type="submit">Confirm</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
